import {
  Box,
  IconButton,
  Table as MUITable,
  TableBody as MUITableBody,
  TableProps as MUITableProps,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { QueryStatus } from "@tanstack/react-query";
import {
  ChevronDown as KeyboardArrowDown,
  ChevronUp as KeyboardArrowUp,
  MoreVertical as MoreVert,
  ArrowUpDown as Sort,
} from "lucide-react";
import PopupState, { bindPopover, bindTrigger } from "material-ui-popup-state";
import { ReactNode, useEffect, useMemo } from "react";
import {
  Column,
  Row,
  TableOptions,
  useExpanded,
  useGlobalFilter,
  useGroupBy,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

import { QueryParamsType } from "api/utils";

// COMPONENTS:
import TableBodyLoading from "./components/TableBodyLoading";
import TableColumnsPopover from "./components/TableColumnsPopover";
import TableEmptyMessage from "./components/TableEmptyMessage";
import TableErrorMessage from "./components/TableErrorMessage";
import TablePagination, {
  TablePageLimitType,
} from "./components/TablePagination";
import useTableFields from "./useTableFields";

export type TableColumn<T extends object = {}> = Column<T> & {
  display?: boolean;
};

export type TableSortingOptions = {
  disable?: boolean;
  remote?: {
    onSortChange: (sortBy: QueryParamsType["sort"]) => void;
  };
};

export type TablePaginationOptions = {
  remote?: {
    pageCount: number | undefined;
    onPageChange: (page: number, limit: TablePageLimitType) => void;
    totalResults?: number;
    initialPageSize?: TablePageLimitType;
    /** When set, table page is controlled by parent (0-based pageIndex). Stops pagination resetting. */
    pageIndex?: number;
  };
};

type TableProps<T extends object> = TableOptions<T> & {
  columns: readonly TableColumn<T>[];
  sort?: TableSortingOptions;
  pagination?: TablePaginationOptions;
  /**
   * Must be memoized, otherwise will reset pagination too frequently
   */
  filters?: unknown;
  status?: QueryStatus;
  // TODO: Improve typescript
  getRowProps?: (row: Row<T>) => object;
  size?: MUITableProps["size"];
  emptyMessage?: string | ReactNode;
  subComponent?: (row: Row<any>) => ReactNode;
  /** When provided, applied after data/page change so rows with notes etc. stay expanded */
  expandedState?: Record<string, boolean>;

  additionalRows?: {
    cells: { value: string | ReactNode }[];
  }[];
};

const Table = <T extends object>({
  columns,
  data,
  initialState,
  // custom props
  sort,
  pagination,
  status,
  getRowProps,
  size,
  emptyMessage,
  filters,
  subComponent,
  expandedState,
  additionalRows,
  // rest
  ...rest
}: TableProps<T>) => {
  // sorting
  const manualSortBy = Boolean(sort?.remote);
  const disableSortBy = Boolean(sort?.disable);
  const onSortChange = sort?.remote?.onSortChange;

  // pagination
  const remotePagination = Boolean(pagination?.remote);
  const remotePageCount = pagination?.remote?.pageCount;
  const totalResults = pagination?.remote?.totalResults;
  const onPageChange = pagination?.remote?.onPageChange;
  const initialPageSize = pagination?.remote?.initialPageSize;
  const controlledPageIndex = pagination?.remote?.pageIndex;
  const isPaginationControlled =
    remotePagination && controlledPageIndex !== undefined;

  const { tableFields, setTableFields } = useTableFields(
    columns
      ?.filter((column) => (column as TableColumn).display !== false)
      .map((column) => column.id || String(column.accessor)),
  );

  const shownColumns = useMemo(() => {
    if (tableFields) {
      const result: TableColumn<T>[] = [];
      tableFields.forEach((columnId) => {
        if (columnId === "_expand") return;
        const foundColumn = columns.find(
          (column) => column.id === columnId || column.accessor === columnId,
        );
        if (foundColumn) result.push(foundColumn);
      });
      return result;
    }
    return columns.filter(
      (col) => (col as TableColumn<T>).id !== "_expand",
    ) as TableColumn<T>[];
  }, [columns, tableFields]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    // pagination
    gotoPage,
    setPageSize,
    pageCount,
    // global filter
    setGlobalFilter,
    // state
    state: { sortBy, pageIndex, pageSize },
    toggleRowExpanded,
    toggleAllRowsExpanded,
  } = useTable(
    {
      columns: shownColumns,
      data,
      manualGroupBy: true,
      // sorting
      disableSortBy: false,
      manualSortBy,
      disableMultiSort: true,
      // pagination
      manualPagination: remotePagination,
      pageCount: remotePageCount,
      // include expanded sub-rows in page so subComponent (e.g. notes row) renders
      paginateExpandedRows: false,
      // global filter,
      manualGlobalFilter: true,
      // initialState
      initialState: { pageSize: initialPageSize || 20, ...initialState },
      // controlled page index for remote pagination so page doesn't reset
      ...(isPaginationControlled && {
        state: { pageIndex: controlledPageIndex },
      }),

      ...rest,
    },

    useGroupBy,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
  );

  // Apply expanded state when data or page changes (e.g. only rows with notes expanded)
  useEffect(() => {
    if (!expandedState || Object.keys(expandedState).length === 0) return;
    toggleAllRowsExpanded(false);
    Object.entries(expandedState).forEach(([id, value]) => {
      if (value) toggleRowExpanded(id as any, true);
    });
  }, [
    data,
    pageIndex,
    expandedState,
    toggleRowExpanded,
    toggleAllRowsExpanded,
  ]);

  /**
   * remote sorting only:
   */
  useEffect(() => {
    if (!disableSortBy && manualSortBy) {
      if (sortBy.length) {
        const sortObject = sortBy[0];
        onSortChange &&
          onSortChange({
            field: sortObject.id,
            order: sortObject.desc ? "desc" : "asc",
          });
      } else {
        onSortChange && onSortChange(undefined);
      }
    }
  }, [sortBy, sort?.remote, onSortChange, disableSortBy, manualSortBy]);

  // necessary to reset page when changing filters
  useEffect(() => {
    setGlobalFilter(filters);
  }, [setGlobalFilter, filters]);

  /**
   * remote pagination only (uncontrolled): sync internal page to parent.
   * When controlled (pageIndex from parent), TablePagination calls onPageChange for page/size changes.
   */
  useEffect(() => {
    if (remotePagination && !isPaginationControlled && onPageChange) {
      onPageChange(pageIndex + 1, pageSize as TablePageLimitType);
    }
  }, [
    remotePagination,
    isPaginationControlled,
    pageIndex,
    pageSize,
    onPageChange,
  ]);

  const isSmallScreen = useMediaQuery<Theme>((t) => t.breakpoints.down("sm"));
  const ROW_HEIGHT = isSmallScreen ? 48 : 60;
  const HEADER_HEIGHT = isSmallScreen ? 48 : 60;
  const BORDER_COLOR = "#F2F2F2";
  const HEADER_BG = "#FFF";

  return (
    <>
      <Box
        sx={{
          overflowX: "auto",
          overflowY: "visible",
          borderTopLeftRadius: 11,
          borderTopRightRadius: 11,

          flex: 1,
          height: "100%",
        }}
      >
        <MUITable
          size={isSmallScreen ? "small" : size}
          {...getTableProps()}
          sx={{
            overflow: "scroll",
            borderRadius: 0,
          }}
        >
          <TableHead
            sx={{
              bgcolor: HEADER_BG,
              borderBottom: `1px solid ${BORDER_COLOR}`,
              "& th": {
                borderColor: BORDER_COLOR,
                borderBottom: "none",
              },
              "& th:first-of-type": {
                borderTopLeftRadius: 11,
                ...(isSmallScreen && {
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: HEADER_BG,
                }),
              },
            }}
          >
            {headerGroups.map((headerGroup, i) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, colIndex) => {
                  const isFirst = colIndex === 0;
                  return (
                    <TableCell
                      sx={{
                        height: HEADER_HEIGHT,
                        fontSize: { xs: 13, sm: 16 },
                        fontWeight: 400,
                        whiteSpace: "nowrap",
                        color: (t: any) =>
                          t.palette.brandText?.secondary ?? "#818181",
                        textAlign: column.align,
                        borderRight: `1px solid ${BORDER_COLOR}`,
                        ...(isFirst ? { borderTopLeftRadius: 11 } : {}),
                        "&:hover": { svg: { color: "primary.main" } },
                        px: { xs: 1, sm: 2 },
                      }}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.render("Header")}
                      {column.canSort && (
                        <Box
                          component="span"
                          sx={{
                            mx: 0.5,
                            fontSize: "1.3em",
                          }}
                        >
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <KeyboardArrowDown
                                size={20}
                                style={{ width: "1em", height: "1em" }}
                              />
                            ) : (
                              <KeyboardArrowUp
                                size={20}
                                style={{ width: "1em", height: "1em" }}
                              />
                            )
                          ) : (
                            <Sort
                              size={20}
                              style={{ width: "1em", height: "1em" }}
                            />
                          )}
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell
                  sx={{
                    height: HEADER_HEIGHT,
                    fontSize: 16,
                    fontWeight: 400,
                    color: (t: any) =>
                      t.palette.brandText?.secondary ?? "#818181",
                    textAlign: "right",
                    svg: { color: "inherit" },
                    width: "20px",
                    minWidth: 48,
                    bgcolor: HEADER_BG,
                    borderRight: "none",
                    borderTopRightRadius: 11,
                    borderBottom: "none",
                    borderLeft: `1px solid ${BORDER_COLOR}`,
                  }}
                >
                  <PopupState
                    variant="popover"
                    // popupId="language-switcher-popover"
                  >
                    {(popupState: any) => (
                      <>
                        <IconButton {...bindTrigger(popupState)}>
                          <MoreVert />
                        </IconButton>
                        <TableColumnsPopover
                          {...bindPopover(popupState)}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                          //@ts-expect-error
                          fields={
                            columns
                              ?.filter((column) => {
                                //@ts-expect-error
                                if (column.display === false) return false;
                                if (column.id === "_expand") return false;
                                return true;
                              })
                              .map((column) => ({
                                value: column.id || String(column.accessor),
                                label: column.Header ?? "",
                                display: tableFields?.includes(
                                  column.id || String(column.accessor),
                                ),
                              })) || []
                          }
                          setFields={(fields) => {
                            setTableFields(fields);
                          }}
                        />
                      </>
                    )}
                  </PopupState>
                </TableCell>
              </TableRow>
            ))}
          </TableHead>

          {/** Status: loading -> displays skeleton */}
          {status === "loading" && (
            <TableBodyLoading
              rows={initialPageSize || 20}
              headerGroups={headerGroups}
            />
          )}

          {/**
           * When status is not used, it will just display rows
           * Status: success -> displays rows
           */}
          {(status === "success" || status === undefined) && (
            <MUITableBody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);

                if (subComponent && row.depth === 1) {
                  return (
                    <Box
                      component="tr"
                      sx={{
                        bgcolor: "grey.100",
                        borderBottom: `1px solid ${BORDER_COLOR}`,
                      }}
                    >
                      {subComponent(row)}
                    </Box>
                  );
                }

                return (
                  <TableRow
                    sx={{
                      bgcolor: "transparent",
                      borderBottom: `1px solid ${BORDER_COLOR}`,
                      transition: "background-color 100ms",
                    }}
                    {...row.getRowProps(getRowProps ? getRowProps(row) : {})}
                  >
                    {row.cells.map((cell, cellIndex) => {
                      const isLastCell = cellIndex === row.cells.length - 1;
                      const isFirstCell = cellIndex === 0;
                      return (
                        <TableCell
                          sx={{
                            height: ROW_HEIGHT,
                            fontSize: { xs: 13, sm: 16 },
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            color: (t: any) =>
                              t.palette.brandText?.primary ?? "#111726",
                            textAlign: cell.column.align,
                            borderRight: isLastCell
                              ? "none"
                              : `1px solid ${BORDER_COLOR}`,
                            px: { xs: 1, sm: 2 },
                            ...(isFirstCell &&
                              isSmallScreen && {
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                bgcolor: "#FFF",
                              }),
                          }}
                          {...cell.getCellProps()}
                        >
                          {row.depth === 1
                            ? cell.render("Cell")
                            : cell.render("Aggregated")}
                        </TableCell>
                      );
                    })}

                    <TableCell
                      sx={{
                        width: "20px",
                        minWidth: 48,
                        borderRight: "none",
                        borderBottom: "inherit",
                      }}
                    />
                  </TableRow>
                );
              })}

              {additionalRows?.map((additionalRow) => (
                <TableRow
                  sx={{
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                  }}
                >
                  {additionalRow.cells.map((cell) => (
                    <TableCell
                      sx={{
                        height: ROW_HEIGHT,
                        fontSize: { xs: 13, sm: 16 },
                        fontWeight: 500,
                        color: (t: any) =>
                          t.palette.brandText?.primary ?? "#111726",
                        px: { xs: 1, sm: 2 },
                      }}
                    >
                      {cell.value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </MUITableBody>
          )}
        </MUITable>

        {/**
         * Status: success and empty data -> displays empty message
         */}
        {status === "success" &&
          data.length === 0 &&
          ((emptyMessage && <>{emptyMessage}</>) || <TableEmptyMessage />)}

        {/**
         * Status: error -> displays error message
         */}
        {status === "error" && <TableErrorMessage />}
      </Box>

      <TablePagination
        gotoPage={gotoPage}
        pageCount={pageCount}
        // @ts-ignore
        pageIndex={isPaginationControlled ? controlledPageIndex : pageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResults={totalResults}
        loading={status === "loading"}
        onRemotePageChange={
          isPaginationControlled
            ? (p) => onPageChange?.(p, pageSize as TablePageLimitType)
            : undefined
        }
        onRemotePageSizeChange={
          isPaginationControlled ? onPageChange : undefined
        }
      />
    </>
  );
};

export default Table;
